import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { Variable, VariableRef } from '@/types/variable';
import { isArray, isNumber, isObject } from '@/util/typeGuards';

export const promptlistUtil = (): ValidationObject<(number | VariableRef)[] | undefined> => {
  const validatePromptlist = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    const values = getValues(configuration, details);
    if (!values?.length && configuration.required) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing required promptlist: ${configuration.label}`,
        id: nodeId
      });
    }
    if (!values) return errors;
    values.map((value) => {
      const isPromptList = !isNumber(value);
      if (isPromptList) {
        const variablesType = application.variables[value.scope];
        const variables =
          variablesType.constructor === Array ? variablesType : (variablesType?.[linkedSubroutineId] as Variable[]);

        const promptlists = variables?.filter((variable) => {
          if (variable.type === 'promptlist') return variable.name;
        });
        if (!promptlists?.length) {
          errors.push({
            severity: 'error',
            message: `Block ${blockName} refers to a promptlist in a subroutine without any promptlists.`,
            id: nodeId
          });
        } else {
          const foundPromptlist = promptlists?.find((promptlist) => promptlist.name === value.name);
          if (!foundPromptlist) {
            errors.push({
              severity: 'error',
              message: `Block ${blockName} refers to a promptlist not available in its context.`,
              id: nodeId
            });
          }
        }
      } else {
        if (!application.prompts?.find((prompt) => prompt.id === value)) {
          errors.push({
            severity: 'error',
            message: `Block ${blockName} refers to a prompt that doesn't exist.`,
            id: nodeId
          });
        }
      }
    });
    return errors;
  };

  const getValues = (configuration: Configuration, initialValue: unknown): (number | VariableRef)[] | undefined => {
    if (!initialValue) return;

    const initVal = initialValue as any;
    if (isArray(initVal, ['number', 'string'])) {
      return initVal as (number | VariableRef)[];
    }
    if (
      isArray(initVal[configuration.value], ['number', 'object']) ||
      (isObject(configuration) && isObject(initialValue))
    ) {
      return initVal[configuration.value];
    }
    return;
  };

  return {
    validate: validatePromptlist,
    getValue: getValues
  };
};
